import * as RadixHoverCard from '@radix-ui/react-hover-card';
import { memo, type ReactNode } from 'react';

import { usePortalStore } from '@air/primitive-portal';
import { tailwindVariants } from '@air/utils-tailwind';

const hoverCard = tailwindVariants({
  base: 'z-portal w-full overflow-y-auto rounded-4 bg-surface-1 p-2 shadow-popover outline-none radix-side-bottom:animate-slide-up radix-side-top:animate-slide-down',
});

export type HoverCardProps = RadixHoverCard.HoverCardProps &
  RadixHoverCard.HoverCardContentProps &
  Pick<RadixHoverCard.HoverCardPortalProps, 'container'> & {
    trigger: ReactNode;
  };

export const HoverCard = memo(
  ({
    children,
    className,
    closeDelay,
    collisionPadding = 8,
    container,
    defaultOpen,
    onOpenChange,
    open,
    openDelay,
    sideOffset = 8,
    trigger,
    ...restOfProps
  }: HoverCardProps) => {
    const portal = usePortalStore((state) => state.portal);

    return (
      <RadixHoverCard.Root
        closeDelay={closeDelay}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        open={open}
        openDelay={openDelay}
      >
        <RadixHoverCard.Trigger asChild data-testid="HOVER_CARD_TRIGGER">
          {trigger}
        </RadixHoverCard.Trigger>
        <RadixHoverCard.Portal container={container || portal}>
          <RadixHoverCard.Content
            className={hoverCard({ className })}
            collisionPadding={collisionPadding}
            data-testid="HOVER_CARD"
            {...restOfProps}
            sideOffset={sideOffset}
          >
            {children}
          </RadixHoverCard.Content>
        </RadixHoverCard.Portal>
      </RadixHoverCard.Root>
    );
  },
);

HoverCard.displayName = 'HoverCard';
